import { Button } from "@/components/ui/button";
import {
  getRestaurantDetails,
  resetRestaurantDetailsState,
  restaurantDetailsState,
} from "@/store/restaurantDetailsState";
import { Activity, useEffect, useRef, useState, type FC } from "react";
import { useOutletContext, useParams } from "react-router";
import RestaurantIntroduction from "@/components/RestaurantIntroduction";
import RestaurantMainInformation from "@/components/RestaurantMainInformation";
import RestaurantCompare from "@/components/RestaurantCompare";
import RestaurantRanking from "@/components/RestaurantRanking";
import Text from "@/components/Text";
import type { ViewMode as ViewModeParents } from "../../index";
import { cn, triggerEvent } from "@/lib/utils";
import { Tooltip, TooltipContent } from "@/components/ui/tooltip";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import RestaurantMenu from "@/components/RestaurantMenu";
import {
  Check,
  InfoIcon,
  Lock,
  Plus,
  Settings2,
  ShoppingCart,
  Tag,
} from "lucide-react";
import RestaurantPromotion from "@/components/RestaurantPromotion";
import AddRestaurantToFavorite from "@/components/dialogs/AddRestaurantToFavorite";
import {
  removeRestaurantFromFavoriteList,
  userFavoriteListState,
} from "@/store/userFavoriteListState";
import DrawerAddRestaurantToFavorite from "@/components/drawers/DrawerAddRestaurantToFavorite";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import { useTranslation } from "react-i18next";
import useDragAndScroll from "@/hooks/useDragAndScroll";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";
import { entitlementsState } from "@/store/entitlementsState";
import { NearbyRadius } from "@/API";
import { userRestaurantsState } from "@/store/userRestaurantsState";
import {
  addUserRestaurants,
  resetUserRestaurantsState,
  userAddRestaurantsState,
} from "@/store/userAddRestaurantsState";
import {
  removeUserRestaurant,
  resetRemoveUserRestaurantsState,
  userRemoveRestaurantState,
} from "@/store/userRemoveRestaurantState";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";

export type ViewMode = "general" | "ranking" | "compare" | "menu" | "promotion";

const RestaurantDetails: FC = () => {
  const { restaurantId } = useParams<{ restaurantId?: string }>();
  const [viewModeParents] = useOutletContext<[ViewModeParents]>();
  const { data: entitlements } = entitlementsState();
  const { sm } = useDetectSizeDevice();
  const { t } = useTranslation();
  const refElementToScroll = useRef<HTMLDivElement>(null);
  const { onMouseDown, onMouseLeave, onMouseMoveCapture, onMouseUp } =
    useDragAndScroll({
      elementToScroll: refElementToScroll,
      direction: "horizontal",
    });

  const { data, isLoading, isFetching } = restaurantDetailsState();
  const { data: favoriteListRestaurants } = userFavoriteListState();
  const { data: userRestaurants } = userRestaurantsState();
  const { data: restaurantAdded, isLoading: restaurantIsAdding } =
    userAddRestaurantsState();
  const { data: restaurantRemoved, isLoading: restaurantIsRemoving } =
    userRemoveRestaurantState();
  const { data: stripeSubscription } = currentSubscriptionState();

  const [viewMode, setViewMode] = useState<ViewMode>("ranking");
  const [addFavoriteThisReastaurantId, setAddFavoriteThisReastaurantId] =
    useState<string | null>(null);

  useEffect(() => {
    if (restaurantId) {
      getRestaurantDetails({
        id: restaurantId,
        nearbyRadius: stripeSubscription
          ? NearbyRadius.TWO_KILOMETERS
          : NearbyRadius.TWO_HUNDRED_METERS,
      });

      triggerEvent("card_viewed", {
        restaurant_id: restaurantId,
      });
      triggerEvent("Store Viewed", {
        restaurant_id: restaurantId,
      });
    }

    return () => {
      resetRestaurantDetailsState();
    };
  }, [restaurantId, stripeSubscription]);

  useEffect(() => {
    if (restaurantRemoved) {
      resetRemoveUserRestaurantsState();
    }
    if (restaurantAdded) {
      resetUserRestaurantsState();
    }
  }, [restaurantRemoved, restaurantAdded]);

  const favoriteRestaurantsId = favoriteListRestaurants
    ? Object.values(favoriteListRestaurants)
        .map((favoriteRestaurant) => favoriteRestaurant.restaurants)
        .flat()
        .map((restaurant) => restaurant.id)
    : [];

  const isFavorite = Boolean(
    favoriteRestaurantsId.find((restaurantId) => restaurantId === data?.id),
  );

  const fullRestaurantDetailsActivated = Boolean(
    entitlements?.find(({ name }) => name === "full_restaurant_details"),
  );

  const isUserRestaurant = Boolean(
    userRestaurants?.find(({ id }) => id === restaurantId),
  );

  return (
    <>
      {sm ? (
        <AddRestaurantToFavorite
          open={!!addFavoriteThisReastaurantId}
          onClose={() => setAddFavoriteThisReastaurantId(null)}
          restaurantsId={
            addFavoriteThisReastaurantId ? [addFavoriteThisReastaurantId] : []
          }
        />
      ) : (
        <DrawerAddRestaurantToFavorite
          open={!!addFavoriteThisReastaurantId}
          onClose={() => setAddFavoriteThisReastaurantId(null)}
          restaurantsId={
            addFavoriteThisReastaurantId ? [addFavoriteThisReastaurantId] : []
          }
        />
      )}

      <div
        style={{
          height: sm
            ? "calc(100dvh - var(--height-header))"
            : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
        }}
        className={cn(
          "flex flex-col bg-[#F7F8F9] sm:transition-(--transition-explorer-restaurant-list) w-[100%] sm:w-[50%] p-[16px] sm:p-[24px] gap-[24px] overflow-x-hidden overflow-y-auto sm:overflow-y-hidden",
          viewModeParents === "map" ? "sm:w-[0px] sm:p-[0px]" : "",
        )}
      >
        <div className="flex justify-between">
          <Button
            onClick={() => {
              history.back();
            }}
            variant="secondaryOutlined"
            className="w-[min-content]"
          >
            {t("back")}
          </Button>
          <div className="flex items-center">
            {isFavorite ? (
              <Button
                variant="secondaryOutlined"
                onClick={(e) => {
                  if (isFavorite && restaurantId) {
                    removeRestaurantFromFavoriteList({
                      restaurantId: restaurantId,
                    });
                    e.stopPropagation();
                  }
                }}
              >
                <img
                  width={18}
                  height={18}
                  src={"/svg/favorite-heart-red.svg"}
                />
                {sm && (
                  <Text fontWeight={600} fontSize={14}>
                    {t("added_to_favorites")}
                  </Text>
                )}
              </Button>
            ) : (
              <Button
                variant="secondaryOutlined"
                onClick={(e) => {
                  if (!isFavorite) {
                    setAddFavoriteThisReastaurantId(restaurantId ?? null);
                    e.stopPropagation();
                  }
                }}
              >
                <img width={18} height={18} src={"/svg/favorite-heart.svg"} />
                {sm && t("add_to_favorites")}
              </Button>
            )}
            <Button
              variant="secondaryOutlined"
              disabled={restaurantIsAdding || restaurantIsRemoving}
              onClick={() => {
                if (!isUserRestaurant) {
                  addUserRestaurants([restaurantId ?? ""]);
                } else {
                  removeUserRestaurant(restaurantId ?? "");
                }
              }}
            >
              {isUserRestaurant ? (
                <Check height={18} width={18} color="#44546F" />
              ) : (
                <Plus height={18} width={18} color="#44546F" />
              )}
              {sm && (
                <Text fontWeight={600} fontSize={14}>
                  {isUserRestaurant
                    ? t("added_to_my_restaurants")
                    : t("add_to_my_restaurants")}
                </Text>
              )}
            </Button>
          </div>
        </div>

        <RestaurantIntroduction
          restaurant={
            data
              ? {
                  id: data.id,
                  name: data.name,
                  address: data?.address ?? "",
                  rating: data?.rating ?? "",
                  deliveryTime: data?.deliveryTime ?? undefined,
                  platform: data.platform.toLocaleLowerCase(),
                  image: data?.imageUrl ?? undefined,
                  url: data?.url ?? undefined,
                  krunshScore: data.krunshScore,
                  phone: data.phone ?? undefined,
                  email: data.email ?? undefined,
                }
              : undefined
          }
          isLoading={isLoading}
        />

        <div
          ref={refElementToScroll}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseMoveCapture={(e) =>
            onMouseMoveCapture(e as unknown as MouseEvent)
          }
          className="flex h-[48px] min-h-[48px] items-center overflow-x-auto overflow-y-hidden"
        >
          <div
            onClick={() => {
              setViewMode("ranking");
              triggerEvent("Ranking Viewed");
            }}
            style={
              viewMode === "ranking"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <img height={18} width={18} src="/svg/ranking.svg" />
              </TooltipTrigger>
              <TooltipContent>{t("ranking_description")}</TooltipContent>
            </Tooltip>
            <Text fontWeight={500} fontSize={16} className="leading-[24px]">
              {t("ranking")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("menu");
              } else {
                setActionTriggerNeedCore(true);
              }

              triggerEvent("Menu Viewed");
            }}
            style={
              viewMode === "menu"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ShoppingCart height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>{t("menu_description")}</TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("menu")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("promotion");
              } else {
                setActionTriggerNeedCore(true);
              }

              triggerEvent("Promotion Viewed");
            }}
            style={
              viewMode === "promotion"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Tag height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>{t("promotion_description")}</TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("promotions")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("compare");
              } else {
                setActionTriggerNeedCore(true);
              }

              triggerEvent("Compared Viewed");
            }}
            style={
              viewMode === "compare"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Settings2 height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>{t("compare_description")}</TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("compare")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("general");
              } else {
                setActionTriggerNeedCore(true);
              }

              triggerEvent("General Information Viewed");
            }}
            style={
              viewMode === "general"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>
                  {t("general_information_description")}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("general_information")}
            </Text>
          </div>
        </div>

        <div className="sm:overflow-y-auto">
          <Activity mode={viewMode === "general" ? "visible" : "hidden"}>
            <RestaurantMainInformation
              restaurant={data ?? undefined}
              isLoading={isLoading}
            />
          </Activity>
          <Activity mode={viewMode === "compare" ? "visible" : "hidden"}>
            <RestaurantCompare restaurant={data} />
          </Activity>

          <Activity mode={viewMode === "ranking" ? "visible" : "hidden"}>
            <RestaurantRanking
              isFetching={isFetching}
              isLoading={isLoading}
              restaurant={data ?? undefined}
            />
          </Activity>
          <Activity mode={viewMode === "menu" ? "visible" : "hidden"}>
            <RestaurantMenu restaurant={data ?? null} />
          </Activity>
          <Activity mode={viewMode === "promotion" ? "visible" : "hidden"}>
            <RestaurantPromotion restaurant={data ?? null} />
          </Activity>
        </div>
      </div>
    </>
  );
};

export default RestaurantDetails;
